import { ref, inject } from 'vue';

export interface NotificationMessage {
  message: string;
  type: 'confirm' | 'critical' | 'informative' | 'caution';
  details?: string;
  timeout?: number;
}

export const notificationSymbol = Symbol('notification');


export function useNotification() {
  // Create a singleton instance
  const notification = ref<NotificationMessage | null>(null);
  const show = (params: NotificationMessage) => {
    notification.value = {
      timeout: 5000, // default timeout
      ...params
    };

    if (params.timeout !== 0) {
      setTimeout(() => {
        notification.value = null;
      }, params.timeout || 4000);
    }
  };

  const clear = () => {
    notification.value = null;
  };

  const success = (message: string, details?: string) => {
    show({ message, type: 'confirm', details });
  };

  const error = (message: string, details?: string) => {
    show({ message, type: 'critical', details });
  };

  const info = (message: string, details?: string) => {
    show({ message, type: 'informative', details });
  };

  const warning = (message: string, details?: string) => {
    show({ message, type: 'caution', details });
  };

  return {
    notification,
    show,
    clear,
    success,
    error,
    info,
    warning
  };
}

// Create a single instance that will be used throughout the app
const globalNotification = useNotification();

// Modified injection helper to return the global instance
export function useNotificationInjection() {
  const notificationService = inject(notificationSymbol, globalNotification);
  return notificationService;
}
